import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";

// 에이전트 상세 공통 조각 (Figma ver.6 03-01~03-05): 내용 폭 1136(좌우 152), 섹션 제목 40/400, 제목과 본문 사이 40, 섹션 사이 100.
// 한글은 단어 중간에서 줄바꿈하지 않아요(word-break: keep-all, 설계서 slide 39 공통 3).

export const DETAIL_GUTTER = "mx-auto w-full max-w-[1440px] px-5 md:px-10 xl:px-[152px]";

// first: 바로가기 바 바로 아래 첫 섹션은 64 (Figma ver.7 05 상단 여백). tight: 제목 → 본문 24 ("snug"는 16). titleExtra: 제목 바로 옆(간격 14) 칩, aside: 제목 줄 오른쪽 끝 (워크플로우 범례 · 전체 보기)
export function DetailSection({
  id,
  title,
  first,
  tight,
  children,
  className,
  titleExtra,
  aside,
}: {
  id: string;
  title: ReactNode;
  first?: boolean;
  tight?: boolean | "snug";
  children: ReactNode;
  className?: string;
  titleExtra?: ReactNode;
  aside?: ReactNode;
}) {
  const heading = <h2 className="text-[32px] font-normal leading-[40px] text-nv-text-primary xl:text-[40px] xl:leading-[48px]">{title}</h2>;
  return (
    <section id={`agent-${id}`} className={cn(DETAIL_GUTTER, "scroll-mt-[136px] break-keep", first ? "pt-[64px]" : "pt-[72px] xl:pt-[100px]", className)}>
      {titleExtra || aside ? (
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <div className="flex items-center gap-[14px]">
            {heading}
            {titleExtra}
          </div>
          {aside}
        </div>
      ) : (
        heading
      )}
      <div className={tight === "snug" ? "mt-4" : tight ? "mt-6" : "mt-8 xl:mt-10"}>{children}</div>
    </section>
  );
}

// 20/500 소제목 (도입 대상 · 차별화 포인트 · 구성 기술 …)
export function DetailSubTitle({ children, className }: { children: ReactNode; className?: string }) {
  return <h3 className={cn("text-[20px] font-medium leading-8 text-nv-text-primary", className)}>{children}</h3>;
}

// 13/#6A6F77 라벨 (지원 데이터 · 해결 과제 …)
export function DetailLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("text-[13px] leading-[21px] text-nv-text-tertiary", className)}>{children}</span>;
}

// 연한 회색 칩 (지원 데이터 · 제공 인터페이스): #F5F7F8, 반경 6, 13/21
export function DetailChip({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("inline-flex items-center rounded-nv-pill bg-nv-bg-page px-[10px] py-[3px] text-[13px] leading-[21px] text-nv-text-primary", className)}>{children}</span>;
}

// 회색 사각 태그 (구성 기술): #F5F7F8 + 1px #E6E6E3, 반경 6
export function DetailTag({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn("inline-flex items-center rounded-nv-pill border border-nv-border bg-nv-bg-page px-[10px] py-[3px] text-[13px] leading-[21px] text-nv-text-primary", className)}>
      {children}
    </span>
  );
}

export function ChipGroup({ label, values }: { label: string; values: string[] }) {
  if (!values.length) return null;
  return (
    <div className="flex flex-col gap-[6px]">
      <DetailLabel>{label}</DetailLabel>
      <div className="flex flex-wrap gap-[6px]">
        {values.map((v) => (
          <DetailChip key={v}>{v}</DetailChip>
        ))}
      </div>
    </div>
  );
}

// 설명 문단은 3줄까지만 보이고 넘치면 [더보기] (Figma ver.7 실데이터 대응 규칙 '긴 문단'). 넘치지 않으면 버튼 없이 그대로.
export function ClampText({ children, className }: { children: string; className?: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      if (!open) setOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [children, open]);
  return (
    <div className="flex flex-col items-start gap-1">
      <p ref={ref} className={cn(className, !open && "line-clamp-3")}>
        {children}
      </p>
      {(overflows || open) && (
        <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="text-[14px] leading-[22px] text-nv-text-secondary underline-offset-2 hover:underline">
          {open ? "접기" : "더보기"}
        </button>
      )}
    </div>
  );
}

// 줄바꿈이 들어 있는 원문을 문단으로
export function Paragraphs({ items, className }: { items: string[]; className?: string }) {
  return (
    <div className={cn("flex flex-col gap-[10px]", className)}>
      {items.map((p, i) => (
        <ClampText key={i} className="text-[16px] leading-[26px] text-nv-text-secondary">
          {p}
        </ClampText>
      ))}
    </div>
  );
}

// 윤곽선 필 (내부 에이전트 상세의 개발 단계 · 보안 등급 · 기술 스택)
export function OutlinePill({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn("inline-flex h-6 items-center rounded-nv-pill border border-nv-border bg-nv-surface px-[10px] text-[12px] font-medium leading-[14px] text-nv-text-secondary", className)}>
      {children}
    </span>
  );
}
